import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Sparkles, Loader2, Copy, Check, Clock, AlertCircle, Download, CalendarDays, Wand2, MessageSquare, Trash2, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI-fillable section headings -> backend section ids
const AI_SECTIONS = [
  { id: 'introduction', heading: '## 1. INTRODUCTION', label: 'Introduction' },
  { id: 'background', heading: '## 2. RESEARCH CONTEXT: BACKGROUND', label: 'Background' },
  { id: 'problem', heading: '## 3. THE RESEARCH PROBLEM', label: 'Problem Statement' },
  { id: 'significance', heading: '## 7. SIGNIFICANCE OF THE STUDY', label: 'Significance' },
  { id: 'pilot_study', heading: '## 9.5 PILOT STUDY', label: 'Pilot Study' },
  { id: 'quality_assurance', heading: '## 9.6 VALIDITY, RELIABILITY AND TRUSTWORTHINESS', label: 'Validity, Reliability & Trustworthiness' },
  { id: 'ethics', heading: '## 10. ETHICAL CONSIDERATIONS', label: 'Ethics' },
];

const PLACEHOLDER_MARKER = '[WRITING GUIDE';

const isPlaceholder = (sectionBody) => sectionBody.includes(PLACEHOLDER_MARKER);

const replaceSectionInOutline = (outline, heading, newContent) => {
  const blocks = outline.split(/\n---\n/);
  const updated = blocks.map((block) => {
    if (block.trim().startsWith(heading)) {
      return `${heading}\n\n${newContent.trim()}`;
    }
    return block;
  });
  return updated.join('\n---\n');
};

const ResearchProposal = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isCollaborator = user?.role === 'collaborator';

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [outline, setOutline] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [enhancingSection, setEnhancingSection] = useState(null);
  const [enhancingAll, setEnhancingAll] = useState(false);

  // Comments state
  const [comments, setComments] = useState([]);
  const [newCommentText, setNewCommentText] = useState({});  // keyed by sectionId
  const [postingComment, setPostingComment] = useState(null);

  const [studentDetails, setStudentDetails] = useState({
    student_number: '',
    title_prefix: '',
    surname: '',
    first_name: '',
    registration_date: '',
    programme: '',
    email: '',
    region: '',
    supervisor: ''
  });
  const [topicApprovalDate, setTopicApprovalDate] = useState('');

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProject(res.data);
        if (res.data.proposal_outline) {
          setOutline(res.data.proposal_outline);
          setGeneratedAt(res.data.proposal_generated_at);
        }
        // Load comments in parallel
        try {
          const cRes = await axios.get(`${API}/projects/${projectId}/comments`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setComments(cRes.data || []);
        } catch (_) { /* ignore comment fetch errors */ }
      } catch (err) {
        toast.error('Failed to load project');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId, token, navigate]);

  const addComment = async (sectionId) => {
    const text = (newCommentText[sectionId || 'general'] || '').trim();
    if (!text) {
      toast.error('Write something first');
      return;
    }
    setPostingComment(sectionId || 'general');
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/comments`,
        { text, section_id: sectionId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments((prev) => [...prev, res.data]);
      setNewCommentText((prev) => ({ ...prev, [sectionId || 'general']: '' }));
      toast.success('Comment added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    } finally {
      setPostingComment(null);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const getCommentsForSection = (sectionId) => comments.filter((c) => c.section_id === sectionId);
  const getGeneralComments = () => comments.filter((c) => !c.section_id);

  const persistOutline = async (newOutline) => {
    await axios.patch(`${API}/projects/${projectId}/proposal-outline`, {
      outline: newOutline
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const handleGenerate = async () => {
    if (!project?.title || !project?.research_aim) {
      toast.error('Project needs a title and research aim before generating the proposal');
      return;
    }
    // For first-time generation require the approval date; on regeneration, default to today
    const approvalDate = topicApprovalDate || new Date().toISOString().split('T')[0];
    if (!outline && !topicApprovalDate) {
      toast.error('Please enter the topic approval date to calculate the proposed timetable');
      return;
    }

    setGenerating(true);
    toast.loading('Assembling your research proposal...', { id: 'prop-gen' });

    try {
      const res = await axios.post(`${API}/ai/generate-proposal-outline`, {
        title: project.title,
        aim: project.research_aim,
        objectives: project.research_objectives || '',
        research_type: project.research_type || 'quantitative',
        literature_outline: project.literature_outline || '',
        methodology_outline: project.methodology_outline || '',
        topic_approval_date: approvalDate,
        student_details: studentDetails
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });

      if (!res.data?.outline) throw new Error('No proposal received');

      const generated = res.data.outline;
      setOutline(generated);
      await persistOutline(generated);
      setGeneratedAt(new Date().toISOString());
      toast.success('Proposal assembled. Now click "Generate with AI" on each pending section.', { id: 'prop-gen' });
    } catch (err) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Request timed out. Please try again.', { id: 'prop-gen' });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to generate. Please try again.', { id: 'prop-gen' });
      }
    } finally {
      setGenerating(false);
    }
  };

  const enhanceSection = async (sectionId, heading, options = {}) => {
    const { silent = false } = options;
    if (!silent) setEnhancingSection(sectionId);
    try {
      const res = await axios.post(`${API}/ai/generate-proposal-section`, {
        section: sectionId,
        title: project.title,
        aim: project.research_aim,
        objectives: project.research_objectives || '',
        research_type: project.research_type || 'quantitative'
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000
      });

      if (!res.data?.content) throw new Error('Empty AI response');

      const updated = replaceSectionInOutline(outline, heading, res.data.content);
      setOutline(updated);
      await persistOutline(updated);
      if (!silent) toast.success(`${sectionId} generated`);
      return updated;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || `Failed to generate ${sectionId}`);
      throw err;
    } finally {
      if (!silent) setEnhancingSection(null);
    }
  };

  const enhanceAllSections = async () => {
    setEnhancingAll(true);
    let workingOutline = outline;
    let successCount = 0;
    let failCount = 0;

    for (const sec of AI_SECTIONS) {
      // Skip already-filled sections
      const blocks = workingOutline.split(/\n---\n/);
      const block = blocks.find((b) => b.trim().startsWith(sec.heading));
      if (block && !isPlaceholder(block)) continue;

      setEnhancingSection(sec.id);
      toast.loading(`Generating ${sec.label}...`, { id: 'enhance-all' });
      try {
        const res = await axios.post(`${API}/ai/generate-proposal-section`, {
          section: sec.id,
          title: project.title,
          aim: project.research_aim,
          objectives: project.research_objectives || '',
          research_type: project.research_type || 'quantitative'
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 90000
        });
        if (res.data?.content) {
          workingOutline = replaceSectionInOutline(workingOutline, sec.heading, res.data.content);
          setOutline(workingOutline);
          await persistOutline(workingOutline);
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    setEnhancingSection(null);
    setEnhancingAll(false);

    if (failCount === 0) {
      toast.success(`All ${successCount} sections generated successfully`, { id: 'enhance-all' });
    } else if (successCount > 0) {
      toast.warning(`${successCount} sections generated, ${failCount} failed. Retry the failed ones individually.`, { id: 'enhance-all' });
    } else {
      toast.error('AI generation failed. Please retry.', { id: 'enhance-all' });
    }
  };

  const handleDownloadDocx = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/export-proposal-docx`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Research_Proposal_${(project?.title || 'Draft').substring(0, 30).replace(/\s/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word document downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download. Generate the proposal first.');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outline);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const updateDetail = (key, value) => {
    setStudentDetails((prev) => ({ ...prev, [key]: value }));
  };

  const researchTypeLabel = (project?.research_type || 'quantitative').charAt(0).toUpperCase() + (project?.research_type || 'quantitative').slice(1);

  const missingItems = [];
  if (!project?.research_aim) missingItems.push('Research Aim');
  if (!project?.research_objectives) missingItems.push('Research Objectives');
  if (!project?.literature_outline) missingItems.push('Literature Review (will be included in full)');
  if (!project?.methodology_outline) missingItems.push('Research Design & Methodology (will be included in full)');

  // Compute pending AI sections from current outline
  const pendingSections = outline
    ? AI_SECTIONS.filter((sec) => {
        const blocks = outline.split(/\n---\n/);
        const block = blocks.find((b) => b.trim().startsWith(sec.heading));
        return block && isPlaceholder(block);
      })
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Render outline section-by-section so per-section AI buttons can be inserted
  const renderSectionBody = (sectionText) => {
    return sectionText.split('\n').map((line, i) => {
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-2xl font-serif font-bold text-slate-900 mt-2 mb-4">{line.replace('# ', '')}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-lg font-serif font-semibold text-violet-800 mt-2 mb-3 pb-2 border-b border-violet-100">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-base font-semibold text-slate-800 mt-4 mb-2">{line.replace('### ', '')}</h3>;
      }
      if (line.match(/^\|.*\|$/)) {
        const cells = line.split('|').filter((c) => c.trim() !== '');
        if (cells.every((c) => c.trim().match(/^[-]+$/))) return null;
        return (
          <div key={i} className="grid gap-2 py-1" style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
            {cells.map((cell, j) => (
              <span key={j} className="text-slate-700 text-sm px-2 py-1 bg-slate-50 rounded border border-slate-100"
                    dangerouslySetInnerHTML={{ __html: cell.trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>
        );
      }
      if (line.match(/^\s*[-*]\s/)) {
        const indent = line.match(/^(\s*)/)[1].length;
        const text = line.replace(/^\s*[-*]\s/, '');
        return (
          <div key={i} className={`flex gap-2 ${indent > 4 ? 'ml-8' : indent > 0 ? 'ml-4' : 'ml-0'} py-0.5`}>
            <span className="text-violet-400 mt-0.5 shrink-0">-</span>
            <span className="text-slate-700" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      // Highlight writing guide instructions distinctly
      if (line.trim().startsWith('[WRITING GUIDE')) {
        return <p key={i} className="text-amber-700 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 my-2 text-xs italic rounded">{line}</p>;
      }
      if (line.trim().startsWith('Use the "Generate with AI"')) {
        return null; // Hide this hint - we now show real buttons
      }
      return (
        <p key={i} className="text-slate-700 py-0.5 leading-relaxed"
           dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
      );
    });
  };

  const renderCommentsBlock = (sectionId, label) => {
    const sectionComments = sectionId ? getCommentsForSection(sectionId) : getGeneralComments();
    const key = sectionId || 'general';
    if (sectionComments.length === 0 && !isCollaborator) return null;
    return (
      <div className="my-3" data-testid={`comments-block-${key}`}>
        {sectionComments.length > 0 && (
          <div className="space-y-2 my-3">
            {sectionComments.map((c) => (
              <div
                key={c.id}
                className="border-l-4 border-red-500 bg-red-50 pl-3 pr-3 py-2 rounded text-sm flex items-start justify-between gap-3"
                data-testid={`comment-${c.id}`}
              >
                <div className="flex-1">
                  <div className="text-xs text-red-800 font-semibold mb-0.5">
                    <MessageSquare className="inline w-3 h-3 mr-1" />
                    {c.author_name || c.author_email} · {new Date(c.created_at).toLocaleString()}
                  </div>
                  <p className="text-red-700 font-medium whitespace-pre-wrap">{c.text}</p>
                </div>
                {(user?.id === c.author_id) && (
                  <button
                    onClick={() => deleteComment(c.id)}
                    className="text-red-400 hover:text-red-700 shrink-0"
                    title="Delete my comment"
                    data-testid={`delete-comment-${c.id}-btn`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {isCollaborator && (
          <div className="my-3 p-3 bg-red-50/60 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-xs text-red-800 font-semibold mb-2">
              <MessageSquare className="w-3.5 h-3.5" />
              Add supervisor comment {label ? `on "${label}"` : '(general)'}
            </div>
            <div className="flex gap-2">
              <textarea
                rows={2}
                className="flex-1 text-sm rounded border border-red-300 bg-white px-2 py-1.5 text-red-700 placeholder:text-red-300 focus:border-red-500 focus:outline-none"
                placeholder="Type your feedback (will show in red to the student)..."
                value={newCommentText[key] || ''}
                onChange={(e) => setNewCommentText((prev) => ({ ...prev, [key]: e.target.value }))}
                data-testid={`comment-textarea-${key}`}
              />
              <Button
                size="sm"
                onClick={() => addComment(sectionId)}
                disabled={postingComment === key}
                className="bg-red-600 hover:bg-red-700 text-white shrink-0 self-start"
                data-testid={`add-comment-${key}-btn`}
              >
                {postingComment === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProposalContent = () => {
    if (!outline) return null;
    const blocks = outline.split(/\n---\n/);
    return blocks.map((block, idx) => {
      const trimmed = block.trim();
      const aiSection = AI_SECTIONS.find((s) => trimmed.startsWith(s.heading));

      return (
        <div key={idx} className="mb-2">
          {renderSectionBody(trimmed)}
          {aiSection && isPlaceholder(trimmed) && !isCollaborator && (
            <div className="my-3 p-3 bg-violet-50 border border-violet-200 rounded-lg flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-violet-900">
                <strong>{aiSection.label}</strong> — click to receive AI bullet-point guidance on what to write.
              </div>
              <Button
                size="sm"
                onClick={() => enhanceSection(aiSection.id, aiSection.heading)}
                disabled={enhancingSection === aiSection.id || enhancingAll}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid={`generate-section-${aiSection.id}-btn`}
              >
                {enhancingSection === aiSection.id ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate with AI</>
                )}
              </Button>
            </div>
          )}
          {aiSection && !isPlaceholder(trimmed) && !isCollaborator && (
            <div className="my-2 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => enhanceSection(aiSection.id, aiSection.heading)}
                disabled={enhancingSection === aiSection.id || enhancingAll}
                className="text-xs"
                data-testid={`regenerate-section-${aiSection.id}-btn`}
              >
                {enhancingSection === aiSection.id ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Regenerating...</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" />Regenerate</>
                )}
              </Button>
            </div>
          )}
          {aiSection && renderCommentsBlock(aiSection.id, aiSection.label)}
          {idx < blocks.length - 1 && <hr className="my-6 border-slate-200" />}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(isCollaborator ? '/collaborator-dashboard' : `/project/${projectId}`)}
                className="text-slate-500 hover:text-slate-700"
                data-testid="back-to-project-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">Research Proposal</h1>
            </div>
            {outline && (
              <div className="flex items-center gap-2">
                <Button onClick={handleCopy} variant="outline" size="sm" data-testid="copy-proposal-btn">
                  {copied ? <><Check className="w-4 h-4 mr-1 text-green-500" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                </Button>
                <Button onClick={handleDownloadDocx} disabled={downloading} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="download-docx-btn">
                  {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  Download .docx
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card-dashboard mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-violet-100 rounded-xl">
              <FileText className="w-6 h-6 text-violet-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-serif font-semibold text-slate-900 mb-1" data-testid="project-title">
                {project?.title || 'Untitled Project'}
              </h2>
              <p className="text-sm text-slate-500 mb-2">{researchTypeLabel} Study</p>
              <p className="text-sm text-slate-600">
                Generate a complete research proposal following standard academic guidelines. The proposal includes your
                full literature review and methodology, AI-fillable sections (Introduction, Background, Problem Statement,
                Significance, Pilot Study, Validity/Reliability/Trustworthiness, Ethics), and a calculated 3-month timetable.
                AI generation produces <strong>bullet-point guidance</strong> on what to write — students expand these into prose themselves.
              </p>
            </div>
          </div>
        </div>

        {isCollaborator && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl flex items-start gap-3" data-testid="supervisor-banner">
            <MessageSquare className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">You are viewing this proposal as a supervisor.</p>
              <p className="text-xs text-red-700 mt-0.5">
                Add comments (in red) inline next to any section below, or add a general comment at the top of the proposal. The student will see your feedback on their proposal page.
              </p>
            </div>
          </div>
        )}

        {missingItems.length > 0 && !isCollaborator && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="missing-items-warning">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Recommended before generating:</p>
                <ul className="text-sm text-amber-700 mt-1 space-y-1">
                  {missingItems.map((item, i) => <li key={i}>- {item}</li>)}
                </ul>
                <p className="text-xs text-amber-600 mt-2">
                  The proposal will include placeholder text for missing sections.
                </p>
              </div>
            </div>
          </div>
        )}

        {!outline && !isCollaborator && (
          <div className="card-dashboard mb-6" data-testid="student-details-form">
            <h3 className="font-semibold text-slate-900 mb-4">Section A: General Details</h3>
            <p className="text-sm text-slate-500 mb-4">Fill in your details to be included in the proposal cover page.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Student Number</Label>
                <Input value={studentDetails.student_number} onChange={(e) => updateDetail('student_number', e.target.value)} placeholder="e.g. 12345678" data-testid="input-student-number" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Title (Mr/Ms/Mrs/Dr)</Label>
                <Input value={studentDetails.title_prefix} onChange={(e) => updateDetail('title_prefix', e.target.value)} placeholder="e.g. Mr" data-testid="input-title-prefix" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Surname</Label>
                <Input value={studentDetails.surname} onChange={(e) => updateDetail('surname', e.target.value)} placeholder="Surname" data-testid="input-surname" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">First Name(s)</Label>
                <Input value={studentDetails.first_name} onChange={(e) => updateDetail('first_name', e.target.value)} placeholder="First name" data-testid="input-first-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Programme</Label>
                <Input value={studentDetails.programme} onChange={(e) => updateDetail('programme', e.target.value)} placeholder="MBA / MPA / MEL" data-testid="input-programme" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <Input value={studentDetails.email} onChange={(e) => updateDetail('email', e.target.value)} placeholder="your@email.com" data-testid="input-email" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Region</Label>
                <Input value={studentDetails.region} onChange={(e) => updateDetail('region', e.target.value)} placeholder="e.g. Gauteng" data-testid="input-region" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Supervisor</Label>
                <Input value={studentDetails.supervisor} onChange={(e) => updateDetail('supervisor', e.target.value)} placeholder="Supervisor name" data-testid="input-supervisor" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Registration Date</Label>
                <Input value={studentDetails.registration_date} onChange={(e) => updateDetail('registration_date', e.target.value)} placeholder="e.g. January 2026" data-testid="input-reg-date" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  Topic Approval Date (for timetable)
                </Label>
                <Input
                  type="date"
                  value={topicApprovalDate}
                  onChange={(e) => setTopicApprovalDate(e.target.value)}
                  data-testid="input-approval-date"
                />
                <p className="text-xs text-slate-400 mt-1">The 3-month dissertation timetable will be calculated from this date.</p>
              </div>
            </div>
          </div>
        )}

        {!isCollaborator && (
        <div className="card-dashboard mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900">
                {outline ? 'Regenerate Full Proposal' : 'Generate Research Proposal'}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {outline
                  ? 'Regenerating will overwrite the current proposal and reset all AI-generated sections.'
                  : 'Assemble the proposal structure. AI-fillable sections will then have a "Generate with AI" button.'}
              </p>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating || !project?.title || !project?.research_aim || (!outline && !topicApprovalDate)}
              className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
              data-testid="generate-proposal-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Assembling...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />{outline ? 'Regenerate' : 'Assemble Proposal'}</>
              )}
            </Button>
          </div>
          {!topicApprovalDate && !outline && (
            <p className="text-xs text-red-500 mt-2">Please enter the topic approval date above before generating.</p>
          )}
          {generatedAt && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3 h-3" />
              Last generated: {new Date(generatedAt).toLocaleString()}
            </div>
          )}
        </div>
        )}

        {outline && pendingSections.length > 0 && !isCollaborator && (
          <div className="card-dashboard mb-6 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-violet-600" />
                  AI Sections Pending: {pendingSections.length}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {pendingSections.map((s) => s.label).join(', ')}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Click "Generate All" to fill every pending section, or use individual "Generate with AI" buttons inline below.
                </p>
              </div>
              <Button
                onClick={enhanceAllSections}
                disabled={enhancingAll || enhancingSection !== null}
                className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                data-testid="generate-all-sections-btn"
              >
                {enhancingAll ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" />Generate All Sections</>
                )}
              </Button>
            </div>
          </div>
        )}

        {outline && (
          <div className="card-dashboard" data-testid="proposal-outline-content">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-900 text-lg">Full Research Proposal</h3>
              <Button onClick={handleDownloadDocx} disabled={downloading} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="download-docx-btn-bottom">
                {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Download .docx
              </Button>
            </div>
            {(isCollaborator || getGeneralComments().length > 0) && (
              <div className="mb-6 pb-6 border-b border-slate-200" data-testid="general-comments-block">
                <div className="text-xs uppercase tracking-wide text-red-700 font-semibold mb-2">
                  Supervisor&apos;s overall comments
                </div>
                {renderCommentsBlock(null, '')}
              </div>
            )}
            <div className="prose prose-slate max-w-none text-sm leading-relaxed">
              {renderProposalContent()}
            </div>
          </div>
        )}

        {!outline && !generating && (
          <div className="card-dashboard text-center py-16" data-testid="empty-proposal-state">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Research Proposal Yet</h3>
            <p className="text-slate-500 max-w-lg mx-auto mb-2">
              Fill in your student details and topic approval date above, then click &quot;Assemble Proposal&quot; to create
              the structure. Each AI-fillable section will then have its own &quot;Generate with AI&quot; button.
            </p>
            <p className="text-xs text-slate-400 max-w-md mx-auto">
              The proposal follows standard academic guidelines with all 13 sections, formatting guide, and a calculated
              3-month dissertation timetable. Once complete, download it as a Word document.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResearchProposal;
